import api, { ApiError } from './api';

const buildHeaders = ({ companyId, userId } = {}) => {
  const headers = {};

  if (companyId) {
    headers['x-company-id'] = companyId;
  }

  if (userId) {
    headers['x-user-id'] = userId;
  }

  return headers;
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

const toDateObject = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') {
      const result = value.toDate();
      return Number.isNaN(result?.getTime?.()) ? null : result;
    }

    if (typeof value.seconds === 'number') {
      const millis = value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1_000_000);
      const result = new Date(millis);
      return Number.isNaN(result.getTime()) ? null : result;
    }
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizeMaintenanceRecord = (record = {}) => {
  const scheduledDate = toDateObject(record.scheduledDate);
  const completedDate = toDateObject(record.completedDate);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const scheduledDateISO = scheduledDate ? scheduledDate.toISOString() : null;
  const completedDateISO = completedDate ? completedDate.toISOString() : null;

  let statusCategory = record.status || 'pending';
  if (!['completed', 'cancelled', 'in_progress'].includes(statusCategory)) {
    if (scheduledDate && scheduledDate < today) {
      statusCategory = 'overdue';
    } else if (scheduledDate) {
      statusCategory = 'upcoming';
    }
  }

  const daysUntilDue = scheduledDate
    ? Math.ceil((scheduledDate - today) / (1000 * 60 * 60 * 24))
    : null;

  const actualCost = toNumberOrNull(record.actualCost ?? record.cost);
  const estimatedCost = toNumberOrNull(record.estimatedCost);

  const parts = Array.isArray(record.parts)
    ? record.parts
    : typeof record.parts === 'string'
      ? record.parts.split(',').map((part) => part.trim()).filter(Boolean)
      : [];

  const displayStatus = ['completed', 'in_progress', 'cancelled'].includes(record.status)
    ? record.status
    : statusCategory;

  return {
    id: record.id,
    vehicleId: record.vehicleId,
    companyId: record.companyId,
    type: record.type || 'Maintenance',
    category: record.category || 'routine',
    status: record.status || 'pending',
    statusCategory,
    displayStatus,
    scheduledDate: scheduledDateISO,
    completedDate: completedDateISO,
    priority: record.priority || 'medium',
    estimatedCost: estimatedCost ?? 0,
    actualCost: actualCost ?? 0,
    cost: actualCost ?? 0,
    vendor: record.vendor || '',
    location: record.location || '',
    technician: record.technician || '',
    mileage: toNumberOrNull(record.mileage),
    nextDue: toNumberOrNull(record.nextDue),
    description: record.description || '',
    notes: record.notes || '',
    parts,
    laborHours: toNumberOrNull(record.laborHours),
    daysUntilDue,
    isOverdue: statusCategory === 'overdue',
    workOrderNumber: record.workOrderNumber || null,
    invoiceNumber: record.invoiceNumber || null,
    assignedTo: record.assignedTo || null,
    createdBy: record.createdBy || null,
    createdAt: record.createdAt || null,
    updatedAt: record.updatedAt || null
  };
};

const maintenanceService = {
  async listMaintenanceRecords(params = {}, context = {}) {
    const { companyId, userId } = context;

    if (!companyId) {
      throw new Error('companyId is required to fetch maintenance records');
    }

    try {
      const data = await api.get('/maintenance', {
        params,
        headers: buildHeaders({ companyId, userId })
      });

      const records = Array.isArray(data?.records)
        ? data.records.map(normalizeMaintenanceRecord)
        : [];

      return {
        records,
        pagination: data?.pagination || {
          total: records.length,
          hasMore: false,
          lastDocId: null
        }
      };
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse?.(error) || error;
    }
  },

  async createMaintenanceRecord(payload = {}, context = {}) {
    const { companyId, userId } = context;

    if (!companyId) {
      throw new Error('companyId is required to create maintenance records');
    }

    if (!payload.vehicleId) {
      throw new Error('vehicleId is required to create maintenance records');
    }

    try {
      const data = await api.post('/maintenance', {
        ...payload,
        companyId
      }, {
        headers: buildHeaders({ companyId, userId })
      });

      const recordPayload = data?.record || data;
      return {
        message: data?.message || 'Maintenance record created successfully',
        record: normalizeMaintenanceRecord(recordPayload)
      };
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse?.(error) || error;
    }
  },

  async updateMaintenanceRecord(recordId, payload = {}, context = {}) {
    const { companyId, userId } = context;

    if (!recordId) {
      throw new Error('recordId is required to update maintenance records');
    }

    if (!companyId) {
      throw new Error('companyId is required to update maintenance records');
    }

    try {
      const data = await api.put(`/maintenance/${recordId}`, payload, {
        headers: buildHeaders({ companyId, userId })
      });

      const recordPayload = data?.record || data;
      return {
        message: data?.message || 'Maintenance record updated successfully',
        record: normalizeMaintenanceRecord(recordPayload)
      };
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse?.(error) || error;
    }
  },

  async completeMaintenanceRecord(recordId, payload = {}, context = {}) {
    const { companyId, userId } = context;

    if (!recordId) {
      throw new Error('recordId is required to complete maintenance records');
    }

    if (!companyId) {
      throw new Error('companyId is required to complete maintenance records');
    }

    try {
      const data = await api.put(`/maintenance/${recordId}/complete`, payload, {
        headers: buildHeaders({ companyId, userId })
      });

      const recordPayload = data?.record || data;
      return {
        message: data?.message || 'Maintenance record completed successfully',
        record: normalizeMaintenanceRecord(recordPayload)
      };
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse?.(error) || error;
    }
  }
};

export {
  maintenanceService as default,
  normalizeMaintenanceRecord
};
